import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { PlatzangebotBadge } from './StatusBadge';

const zeit = (d) => (d ? new Date(d).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '–');

// Platzangebote eines Wartelisten-Eintrags: aktuelles Angebot mit Antwort, Verlauf, Aktionen
export default function PlatzangebotInfo({ wartelisteId, darfBearbeiten, onGeaendert }) {
  const [angebote, setAngebote] = useState(null);
  const [fehler, setFehler] = useState('');
  const [arbeitet, setArbeitet] = useState(false);
  const [verlaufOffen, setVerlaufOffen] = useState(false);

  const laden = useCallback(() => {
    api.get(`/api/admin/warteliste/${wartelisteId}/platzangebote`)
      .then(r => setAngebote(r.data))
      .catch(() => setFehler('Platzangebote konnten nicht geladen werden'));
  }, [wartelisteId]);

  useEffect(() => { laden(); }, [laden]);

  if (!angebote || angebote.length === 0) return fehler ? <div className="alert alert-error">{fehler}</div> : null;

  const aktuell = angebote[0];
  const offen = ['offen', 'abgelaufen'].includes(aktuell.zustand);

  const aktion = async (pfad, daten, frage) => {
    if (!window.confirm(frage)) return;
    setArbeitet(true);
    setFehler('');
    try {
      await api.post(`/api/admin/warteliste/${wartelisteId}/platzangebot/${pfad}`, daten);
      laden();
      if (onGeaendert) onGeaendert();
    } catch (err) {
      setFehler(err.response?.data?.error || 'Aktion fehlgeschlagen');
      laden();
    } finally {
      setArbeitet(false);
    }
  };

  return (
    <>
      <div className="detail-section">Platzangebot</div>
      <div style={{ background: '#f8f9fa', border: '1px solid var(--border)', borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
          <PlatzangebotBadge zustand={aktuell.zustand} />
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            gesendet am {zeit(aktuell.gesendet_am)}{aktuell.gesendet_von_name ? ` von ${aktuell.gesendet_von_name}` : ''} an {aktuell.gesendet_an}
          </span>
        </div>
        <div style={{ fontSize: '0.85rem' }}>
          {aktuell.antwort ? (
            <>Antwort <strong>{aktuell.antwort === 'angenommen' ? 'angenommen' : 'ausgeschlagen'}</strong> am {zeit(aktuell.beantwortet_am)}
              {aktuell.beantwortet_ueber === 'link'
                ? ' – über den Link in der E-Mail'
                : ` – manuell eingetragen${aktuell.beantwortet_von_name ? ` von ${aktuell.beantwortet_von_name}` : ''}`}</>
          ) : aktuell.zustand === 'zurueckgezogen' ? (
            <>Zurückgezogen am {zeit(aktuell.widerrufen_am)}{aktuell.widerrufen_von_name ? ` von ${aktuell.widerrufen_von_name}` : ''} – der Link ist ungültig.</>
          ) : (
            <>Antwortfrist bis {zeit(new Date(new Date(aktuell.gueltig_bis).getTime() - 60000))}{aktuell.zustand === 'abgelaufen' ? ' – abgelaufen, der Link nimmt keine Antwort mehr an.' : ''}</>
          )}
        </div>

        {darfBearbeiten && offen && (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
            <button className="btn btn-secondary btn-sm" disabled={arbeitet}
              onClick={() => aktion('antwort', { antwort: 'angenommen' }, 'Antwort „Angenommen" eintragen (z.B. nach einem Telefonat)?\n\nDer Link in der E-Mail ist danach nicht mehr nutzbar.')}>
              ✓ Als angenommen eintragen
            </button>
            <button className="btn btn-secondary btn-sm" disabled={arbeitet}
              onClick={() => aktion('antwort', { antwort: 'ausgeschlagen' }, 'Antwort „Ausgeschlagen" eintragen?\n\nDer Link in der E-Mail ist danach nicht mehr nutzbar.')}>
              ✕ Als ausgeschlagen eintragen
            </button>
            <button className="btn btn-danger btn-sm" disabled={arbeitet}
              onClick={() => aktion('zurueckziehen', {}, 'Platzangebot zurückziehen?\n\nDer Link in der E-Mail wird ungültig, eine Antwort ist dann nicht mehr möglich.')}>
              Angebot zurückziehen
            </button>
          </div>
        )}

        {fehler && <div className="alert alert-error" style={{ marginTop: '0.6rem', marginBottom: 0 }}>{fehler}</div>}

        {angebote.length > 1 && (
          <div style={{ marginTop: '0.6rem' }}>
            <button type="button" onClick={() => setVerlaufOffen(o => !o)}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--primary)', cursor: 'pointer', fontSize: '0.8rem' }}>
              {verlaufOffen ? '▾' : '▸'} Frühere Angebote ({angebote.length - 1})
            </button>
            {verlaufOffen && angebote.slice(1).map(a => (
              <div key={a.id} style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
                <PlatzangebotBadge zustand={a.zustand} /> gesendet {zeit(a.gesendet_am)}
                {a.beantwortet_am && `, beantwortet ${zeit(a.beantwortet_am)}`}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
